/* El control de llaves (Nico, 26/10/2026, ticket 522): «¿podré generar QR para
 * las llaves y que cuando las pidan o las entreguen las puedan escanear? Lo de
 * las llaves es un problema cotidiano y sería muy bueno resolverlo, pero
 * simplemente anotar no soluciona nada».
 *
 * ── LO QUE YA HABÍA, Y QUÉ SIGNIFICA CADA PALABRA ─────────────────────────
 *
 * Los movimientos viven en `eventos` desde CantApp: `area = 'porteria'`,
 * `tipo` RETIRA o ENTREGA, `persona` (el DNI de quien se la lleva o la trae),
 * `operador` (el sereno que la dio) y `tractor`, una LISTA de internos —a
 * veces se llevan dos llaves juntas—. Se leyeron las secuencias reales del
 * 21/10 al 26/10 y el sentido es éste:
 *
 *   · RETIRA: la persona se LLEVA la llave de portería;
 *   · ENTREGA: la persona la DEVUELVE a portería.
 *
 * (El 1030: RETIRA 18:53 y ENTREGA 20:47, la misma persona.) Por eso lo que
 * «anotar no soluciona» se resuelve con el último movimiento de cada interno:
 * si es un RETIRA, la llave la tiene esa persona desde esa hora; si es una
 * ENTREGA, está en portería.
 *
 * ── LAS FILAS VIEJAS NO CUENTAN ───────────────────────────────────────────
 *
 * Lo heredado de Firestore trae la fecha como objeto (`{"seconds": …}`), no
 * como texto ISO. Ésas no se pueden ordenar contra las nuevas y además son de
 * meses atrás: una llave retirada en junio y nunca anotada de vuelta no es una
 * llave que alguien tiene hoy. Se descartan acá, en un solo lugar. */

export const RETIRA = "RETIRA";
export const ENTREGA = "ENTREGA";

/* Una llave afuera más de esto se marca fuerte. Doce horas cubre un turno
 * entero y una noche («lo uso para dormir esta noche» es un retiro real del
 * 23/10): pasado eso ya no es un movimiento en playa, es una llave que no
 * volvió. */
export const HORAS_DEMORA = 12;

const fechaDe = (v) => {
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}T/.test(v)) return null;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : new Date(t);
};

/* Los internos de un movimiento. `tractor` llega como lista o como el texto
 * JSON de una lista (así lo guarda la columna); un interno suelto también se
 * entiende. Siempre texto, sin espacios: «1004», no 1004. */
export const internosDe = (evento) => {
  let v = evento?.tractor;
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return [];
    if (s.startsWith("[")) {
      try {
        v = JSON.parse(s);
      } catch {
        return [];
      }
    } else {
      v = [s];
    }
  }
  if (v === null || v === undefined) return [];
  if (!Array.isArray(v)) v = [v];
  return v.map((x) => String(x ?? "").trim()).filter(Boolean);
};

/* El estado de cada llave según su último movimiento:
 * `{ [interno]: { afuera, persona, desde, evento } }`. Sólo los movimientos de
 * llaves con fecha ISO; los demás se ignoran. */
export const estadoLlaves = (eventos = []) => {
  const estado = {};
  for (const ev of eventos || []) {
    if (ev?.tipo !== RETIRA && ev?.tipo !== ENTREGA) continue;
    const desde = fechaDe(ev.fecha);
    if (!desde) continue;
    for (const interno of internosDe(ev)) {
      const antes = estado[interno];
      if (antes && antes.desde >= desde) continue;
      estado[interno] = {
        afuera: ev.tipo === RETIRA,
        persona: ev.persona ? String(ev.persona) : null,
        desde,
        evento: ev,
      };
    }
  }
  return estado;
};

/* QUIÉN ESCANEA (Igna, 27/10/2026): el QR lo puede escanear el que se lleva
 * la llave, con su propio usuario del hub, y entonces es él: no se le
 * pregunta. Sólo desde la cuenta de portería (la de la barrera, área
 * «porteria», sin DNI propio) se pregunta quién se la lleva o quién la trae.
 * Devuelve el DNI del que escanea —el `id` de `personas`— o null si hay que
 * preguntar: la cuenta de portería, un usuario sin DNI cargado, o sin sesión. */
export const AREA_PORTERIA = "porteria";
export const quienEscanea = (usuario) => {
  if (!usuario) return null;
  if (String(usuario.area || "").trim().toLowerCase() === AREA_PORTERIA) return null;
  const dni = String(usuario.dni ?? "").replace(/\D/g, "");
  return dni && !/^0+$/.test(dni) ? dni : null;
};

/* Lo que corresponde anotar ahora para esa llave: si está afuera, que vuelve;
 * si está en portería (o nunca se anotó), que se la llevan. */
export const proximoMovimiento = (est) => (est?.afuera ? ENTREGA : RETIRA);

export const horasAfuera = (est, ahora = new Date()) =>
  est?.afuera ? (ahora - est.desde) / 3600000 : 0;

export const demorada = (est, ahora = new Date()) =>
  horasAfuera(est, ahora) >= HORAS_DEMORA;

/* Las llaves que están afuera, la que hace más tiempo primero: es la que hay
 * que salir a buscar. */
export const afuera = (estado, ahora = new Date()) =>
  Object.entries(estado || {})
    .filter(([, e]) => e.afuera)
    .map(([interno, e]) => ({ interno, ...e, horas: horasAfuera(e, ahora) }))
    .sort((a, b) => a.desde - b.desde);

/* «hace 40 min», «hace 3 h», «hace 2 días». */
export const hace = (desde, ahora = new Date()) => {
  const min = Math.max(0, Math.floor((ahora - desde) / 60000));
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `hace ${h} h`;
  return `hace ${Math.floor(h / 24)} días`;
};

/* La ruta de la llave en el hub: la que abre un gancho del tablero. El
 * interno va codificado: hay internos con letras. */
export const rutaLlave = (interno) => `/llaves/${encodeURIComponent(String(interno))}`;

/* EL QR ABRE WHATSAPP CON CEPI (Igna, 27/10/2026): las llaves son para los
 * choferes, y los choferes no tienen usuario del hub. Escanear la etiqueta
 * abre el chat de Cepi con el mensaje ya escrito; Cepi lo reconoce y pregunta
 * «¿Me la llevo / La dejo?» (en el bot, `llaves_wa.py`).
 *
 * EL TEXTO ES EL CONTRATO CON EL BOT: «Llave del interno 1030 - Patente
 * AE123CD», la patente en mayúsculas y sin espacios; sin patente, sólo el
 * interno. Si se cambia acá, se cambia allá. El número es el de la empresa en
 * WhatsApp (el de Meta), no un dato privado. */
export const CEPI_WHATSAPP = "5491178217599";

export const textoQr = (interno, dominio) => {
  const pat = String(dominio ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const base = `Llave del interno ${String(interno ?? "").trim()}`;
  return pat ? `${base} - Patente ${pat}` : base;
};

export const urlLlave = (interno, dominio) =>
  `https://wa.me/${CEPI_WHATSAPP}?text=${encodeURIComponent(textoQr(interno, dominio))}`;

/* EL TABLERO: un gancho por tractor activo, en orden de interno, con su llave
 * colgada (en portería) o el gancho vacío (afuera, con quién la tiene). Una
 * llave afuera de un interno que ya no está activo también va: que no esté en
 * la lista de activos no la trae de vuelta. */
export const ganchos = (tractores = [], estado = {}) => {
  const vistos = new Map();
  for (const t of tractores || []) {
    const interno = String(t?.id ?? "").trim();
    if (interno && !vistos.has(interno)) vistos.set(interno, t.dominio || "");
  }
  for (const [interno, e] of Object.entries(estado || {})) {
    if (e?.afuera && !vistos.has(interno)) vistos.set(interno, "");
  }
  return [...vistos.entries()]
    .sort(([a], [b]) => a.localeCompare(b, "es", { numeric: true }))
    .map(([interno, dominio]) => {
      const e = estado?.[interno] || null;
      return { interno, dominio, afuera: Boolean(e?.afuera), est: e };
    });
};

/* LA CONFIRMACIÓN DEL CHOFER (Igna, 27/10/2026). Cuando portería le entrega
 * una llave a un chofer, el chofer la confirma por WhatsApp; mientras no lo
 * hace, la llave va marcada. Lo lleva el bot en `llaves_confirmaciones`, una
 * fila por RETIRA (`evento_id`). La marca vale sólo para el ÚLTIMO movimiento
 * de la llave: una entrega vieja sin confirmar no marca una llave que ya
 * volvió o que se llevó otro.
 *
 *   null        nada que marcar (confirmada, cerrada, o no hay pedido)
 *   { tipo: "sin", texto: "sin confirmar" }
 *   { tipo: "no",  texto: "dijo que no" }
 * y `alerta: true` si ya salió el aviso (va en rojo, como una demorada). */
export const confirmacionDe = (est, confirmaciones) => {
  if (!est?.afuera || est.evento?.tipo !== RETIRA || !est.evento?.id) return null;
  const c = (confirmaciones || []).find((x) => String(x?.evento_id) === String(est.evento.id));
  if (!c || c.confirmado_en || c.cerrado_en) return null;
  const alerta = Boolean(c.alerta_en);
  return c.rechazado_en
    ? { tipo: "no", texto: "dijo que no", alerta }
    : { tipo: "sin", texto: "sin confirmar", alerta };
};

/* La ruta de las etiquetas con sólo esos internos: «Imprimir» desde una llave. */
export const rutaEtiquetas = (interno) =>
  `/llaves/etiquetas${interno ? `?i=${encodeURIComponent(String(interno))}` : ""}`;

/* La fila que se guarda en `eventos`, la misma forma que el formulario de
 * siempre (`submitMovimientoLlaves`): así lo que se anota escaneando aparece
 * en los mismos listados que lo que se anota a mano. */
export const filaMovimiento = ({ tipo, interno, persona, operador, detalle }) => ({
  area: "porteria",
  tipo,
  persona: persona ? String(persona) : null,
  operador: operador ? String(operador) : null,
  tractor: [String(interno)],
  detalle: detalle ? String(detalle).trim() || null : null,
});
